import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NavUser } from "./NavUser";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useStore } from "@/hooks/useStore";
import { useLogout } from "@/hooks/useLogout";
import { user } from "@/endpoints/dryp/user";
import { UserStorage } from "@/storage/UserStorage";
import {
	ActivityIcon,
	ArrowDownCircleIcon,
	BuildingIcon,
	CheckIcon,
	DropletIcon,
	FolderIcon,
	LayoutDashboardIcon,
	MapIcon,
	UsersIcon,
	ConstructionIcon,
	BellIcon,
} from "lucide-react";
import { SidebarCollapsibleGroup } from "./SidebarCollapsibleGroup";
import { Link } from "react-router";
import { Routes } from "@/config/routes";
import { LensLogo } from "@/components/LensLogo";
import { SearchIcon } from "lucide-react";
import { UserOrganizationAccess } from "@/endpoints/dryp/user";

interface OrganizationSwitcherProps {
	organizations: UserOrganizationAccess[];
	currentOrganizationId?: Dryp.OrganizationId;
	organizationName?: string;
	onSwitch: (id: Dryp.OrganizationId) => void;
}

const OrganizationSwitcher = ({
	organizations,
	currentOrganizationId,
	organizationName,
	onSwitch,
}: OrganizationSwitcherProps) => {
	const [search, setSearch] = useState("");
	const showSearch = organizations.length > 5;

	const filtered = search
		? organizations.filter(org => org.organizationName.toLowerCase().includes(search.toLowerCase()))
		: organizations;

	return (
		<DropdownMenu
			onOpenChange={open => {
				if (!open) setSearch("");
			}}
		>
			<DropdownMenuTrigger asChild>
				<SidebarMenuButton className="data-[slot=sidebar-menu-button]:!p-1.5">
					<ArrowDownCircleIcon className="h-5 w-5" />
					<span className="text-base font-semibold">{organizationName}</span>
				</SidebarMenuButton>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-56">
				{showSearch && (
					<div className="flex items-center gap-2 px-3 py-2 border-b mb-1">
						<SearchIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
						<input
							value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder="Search..."
							className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
							autoFocus
							onKeyDown={e => e.stopPropagation()}
						/>
					</div>
				)}
				<div className="max-h-64 overflow-y-auto">
					{filtered.length === 0 ? (
						<div className="px-2 py-3 text-xs text-muted-foreground text-center">No results</div>
					) : (
						filtered.map(org => {
							const isActive = org.organizationId === currentOrganizationId;
							return (
								<DropdownMenuItem
									key={org.organizationId}
									onClick={() => !isActive && onSwitch(org.organizationId)}
									className={
										isActive ? "bg-accent font-semibold" : org.primaryOrganization ? "bg-muted" : ""
									}
								>
									<span className="flex-1">{org.organizationName}</span>
									{org.primaryOrganization && (
										<span className="text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">
											Primary
										</span>
									)}
									{isActive && <CheckIcon className="h-4 w-4" />}
								</DropdownMenuItem>
							);
						})
					)}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export const AppSidebar = observer(() => {
	const store = useStore();
	const userName = store.userStore.getUser?.name || "";
	const organizationName = store.userStore.getOrganization?.Name;
	const currentOrganizationId = store.userStore.getOrganization?.Id;
	const logout = useLogout();
	const organizations = store.userStore.getOrganizations;

	const [switching, setSwitching] = useState(false);

	const handleSwitchOrganization = async (organizationId: Dryp.OrganizationId) => {
		setSwitching(true);
		const response = await user.switchOrganization(organizationId);
		UserStorage.setToken(response.token);
		UserStorage.setRefreshToken(response.refreshToken);
		window.location.href = Routes.Dashboard.Home.path;
	};

	return (
		<>
			{switching && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
					<div className="flex flex-col items-center gap-3">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
						<p className="text-sm text-muted-foreground">Switching organization...</p>
					</div>
				</div>
			)}
			<Sidebar variant="inset" className="transition-all duration-200">
				<SidebarHeader className="pt-6 pb-6">
					<div className="flex justify-center mb-4">
						<LensLogo width="50%" color="hsl(var(--sidebar-foreground))" />
					</div>
					<SidebarMenu>
						<SidebarMenuItem>
							{!organizations.length ? (
								<div className="flex h-8 items-center gap-2 rounded-md px-2">
									<div className="size-4 animate-pulse rounded-md bg-sidebar-foreground/20" />
									<div className="h-4 flex-1 animate-pulse rounded-md bg-sidebar-foreground/20" />
								</div>
							) : (
								<OrganizationSwitcher
									organizations={organizations}
									currentOrganizationId={currentOrganizationId}
									organizationName={organizationName}
									onSwitch={handleSwitchOrganization}
								/>
							)}
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarHeader>

				<SidebarContent>
					<SidebarCollapsibleGroup label="Overview">
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Home.path} className="flex items-center gap-2">
										<LayoutDashboardIcon className="size-4" />
										<span>Home</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link
										to={Routes.Dashboard.Structures.Structures.path}
										className="flex items-center gap-2"
									>
										<BuildingIcon className="size-4" />
										<span>Structures</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Events.path} className="flex items-center gap-2">
										<ActivityIcon className="size-4" />
										<span>Events</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Map.path} className="flex items-center gap-2">
										<MapIcon className="size-4" />
										<span>Map</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarCollapsibleGroup>

					<SidebarCollapsibleGroup label="Analytics">
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link
										to={Routes.Dashboard.InfiltrationInflow.path}
										className="flex items-center gap-2"
									>
										<DropletIcon className="size-4" />
										<span>Infiltration &amp; Inflow</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Blockages.path} className="flex items-center gap-2">
										<ConstructionIcon className="size-4" />
										<span>Blockages</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarCollapsibleGroup>

					<SidebarCollapsibleGroup label="Organization">
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Admin.Groups.path} className="flex items-center gap-2">
										<FolderIcon className="size-4" />
										<span>Groups</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Alarms.path} className="flex items-center gap-2">
										<BellIcon className="size-4" />
										<span>Alarms</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>

							<SidebarMenuItem>
								<SidebarMenuButton size="sm" asChild>
									<Link to={Routes.Dashboard.Admin.Users.path} className="flex items-center gap-2">
										<UsersIcon className="size-4" />
										<span>Users</span>
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarCollapsibleGroup>

					{store.userStore.isSuperAdmin && (
						<SidebarCollapsibleGroup label="Backoffice">
							<SidebarMenu>
								<SidebarMenuItem>
									<SidebarMenuButton size="sm" asChild>
										<Link
											to={Routes.Dashboard.Admin.Organizations.path}
											className="flex items-center gap-2"
										>
											<BuildingIcon className="size-4" />
											<span>Organizations</span>
										</Link>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton size="sm" asChild>
										<Link
											to={Routes.Dashboard.Admin.BackofficeUsers.path}
											className="flex items-center gap-2"
										>
											<UsersIcon className="size-4" />
											<span>Users</span>
										</Link>
									</SidebarMenuButton>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarCollapsibleGroup>
					)}
				</SidebarContent>

				<SidebarFooter>
					<NavUser onLogOut={logout} user={userName.toString()} />
				</SidebarFooter>
			</Sidebar>
		</>
	);
});
